import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { splunkInstance } from '@/common/services/splunk';
import { buildSubstituteDoctorSearchText } from '@/common/utils/buildSubstituteDoctorSearch';

interface OnlineVisitSuggestionBannerProps {
  expertises: any;
  doctorCity?: string;
  slug: string;
}

/**
 * Shown under the booking card when the doctor has no bookable channel at all — in-person or
 * online — replacing the old named SubstituteDoctorsInline there for the flagged population
 * (owner rollout, 2026-10-17, phase 1). Deliberately carries no other doctor's name here: the
 * owner asked that this doctor never be made to look replaced on their own profile. Naming the
 * substitute is deferred to /online-visit-suggestion, one click away.
 *
 * View + click events both fire here (not just click) so the owner can later compute a real
 * click-through rate, not just a raw click count with an unknown denominator.
 */
const OnlineVisitSuggestionBanner = ({ expertises, doctorCity, slug }: OnlineVisitSuggestionBannerProps) => {
  const groupName = expertises?.group_expertises?.[0]?.name;
  const text = buildSubstituteDoctorSearchText(expertises?.expertises);
  const href = `/online-visit-suggestion/?${new URLSearchParams({
    text,
    city: doctorCity || 'ir',
    ...(groupName ? { group: groupName } : {}),
    from: slug,
  }).toString()}`;

  const viewSentFor = useRef<string | null>(null);
  useEffect(() => {
    if (!slug || viewSentFor.current === slug) return;
    viewSentFor.current = slug;
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-view-banner',
      event: { slug, variant: 'no-online-visit-banner' },
    });
  }, [slug]);

  const handleClick = () => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-banner',
      event: { slug, variant: 'no-online-visit-banner' },
    });
  };

  return (
    <Link
      href={href}
      onClick={handleClick}
      className="block rounded-xl md:rounded-2xl p-4 md:p-5 text-white shadow-lg"
      style={{ background: 'linear-gradient(135deg, #3861FB, #2A4FD9)', boxShadow: '0px 10px 25px rgba(56,97,251,0.35)' }}
    >
      <div className="flex items-center gap-2.5 mb-2.5">
        <span className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/[0.18] text-base">
          💬
        </span>
        <span className="text-sm md:text-base font-bold">
          {groupName ? `یک متخصص ${groupName} دیگر` : 'یک متخصص دیگر'}، همین الان آنلاین پاسخگوست
        </span>
      </div>
      <p className="text-xs md:text-sm text-[#DCE4FF] mb-2.5">
        اگر عجله دارید، می‌توانید با یک متخصص دیگر مشورت کنید
      </p>
      <ul className="flex flex-col gap-1.5 mb-3 text-xs md:text-sm text-[#DCE4FF]">
        <li className="flex items-center gap-1.5"><span>✓</span><span>همین الان، بدون نوبتِ روزها بعد</span></li>
        <li className="flex items-center gap-1.5"><span>✓</span><span>بدون رفت‌وآمد و بدون صف انتظار</span></li>
        <li className="flex items-center gap-1.5"><span>✓</span><span>پیگیری در همان گفت‌وگو، هر زمان که بخواهید</span></li>
      </ul>
      <div className="flex items-center justify-between rounded-lg bg-white/[0.14] px-3 py-2.5">
        <span className="text-xs md:text-sm font-semibold">دیدن این متخصص و شروع ویزیت آنلاین</span>
        <span>&larr;</span>
      </div>
    </Link>
  );
};

export default OnlineVisitSuggestionBanner;
